import { MAX_CHAT_PACKET, MicrophoneDidNotOpenError, ROOM_CHANGED, chatIdOf, chatTextOf, followPacket, readFollow } from '@versesverse/core';
import type { FollowView } from '@versesverse/core';
import { answeredWithin } from './deadline';
import type { ChatMessage, RoomDeparture, RoomParticipant, VoiceConnector, WholeSession } from './voiceSession';

/** What we put on the wire. Kept minimal — the sender is known from the packet, not the payload. */
type WirePayload =
  /** What somebody typed (D12). */
  | { kind: 'chat'; text: string; at: number }
  /** The room changed; ask the server what it is now. Carries no state on purpose. */
  | { kind: typeof ROOM_CHANGED; at: number };

/**
 * Read a data packet as a message, or null.
 *
 * Everything here is arriving from another participant's browser, so nothing is trusted: not that
 * it is JSON, not that it has the right fields, not that `text` is a string, and not that it is a
 * sane length. A malformed packet is dropped rather than thrown, because one bad sender must not
 * break the chat for everyone else in the room.
 */
export function readPayload(bytes: Uint8Array): WirePayload | null {
  /*
   * BEFORE DECODING ANYTHING — the length was the one thing on that list nothing checked.
   *
   * The comment above already said a packet's length is not trusted, and the check for it ran
   * after `TextDecoder` and `JSON.parse` had both been handed the whole thing. Neither is
   * interruptible, so a sender putting fifty megabytes on the wire stopped every tab in the room,
   * the audio element with it — one bad sender breaking the chat for everyone else, which is the
   * exact harm the rest of this function is written to prevent.
   */
  if (bytes.byteLength > MAX_CHAT_PACKET) return null;
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    if (typeof parsed !== 'object' || parsed === null) return null;
    /* Narrowed by `in` rather than asserted with a cast: "does this object have a field called
       kind" is a question TypeScript can answer, and answering it is the whole job here. */
    const kind = 'kind' in parsed ? parsed.kind : undefined;
    const at = 'at' in parsed ? parsed.at : undefined;
    const when = typeof at === 'number' ? at : Date.now();
    /* A nudge, carrying nothing anybody has to believe — sent by the server, see `room-wire.ts`. */
    if (kind === ROOM_CHANGED) return { kind: ROOM_CHANGED, at: when };
    /*
     * WHAT A CHAT WILL CARRY IS DECIDED ONCE — `chatTextOf` in core, for every chat this product
     * grows (`COMMUNICATION.md §8.1`). It drops anything that is not text, anything that is only
     * spaces, and clamps a pasted essay to the one length every chat shares — and it returns null
     * rather than throwing, because one bad sender must not break the conversation for the room.
     */
    const text = chatTextOf('text' in parsed ? parsed.text : undefined);
    if (text === null) return null;
    return { kind: 'chat', text, at: when };
  } catch {
    return null;
  }
}

/** The numeric codes the SFU sends — the enum’s own value union, never written out by hand. */
type LiveKitDisconnectCode = typeof import('livekit-client').DisconnectReason[keyof typeof import('livekit-client').DisconnectReason];

/**
 * WHY A ROOM ENDED, from the code the SFU sends.
 *
 * Three of the four are not network trouble at all — a host removing somebody, a host closing the
 * room, and the same account arriving on a second device — and saying "the connection was lost" for
 * any of them is false. For the first it hides a moderation decision from the only person it is
 * about; for the third it sends a reader who has just opened the room on their phone to go and
 * check their wifi.
 *
 * THE ENUM IS PASSED IN rather than imported, and that is the whole reason this is a function.
 * `DisconnectReason` is needed for its VALUES — the numbers are the wire format, and writing 4 and
 * 5 here would be the enum copied by hand — but importing it at module scope would pull the LiveKit
 * SDK into the bundle of every reader who never opens a room, which this file exists to avoid. So
 * the caller, which has just awaited the dynamic import, hands it over.
 *
 * Two of the four sentences this chooses between had never been watched by anything.
 */
export function departureFrom(
  reason: LiveKitDisconnectCode | undefined,
  reasons: typeof import('livekit-client').DisconnectReason,
): RoomDeparture {
  if (reason === reasons.PARTICIPANT_REMOVED) return 'removed';
  if (reason === reasons.ROOM_DELETED) return 'roomClosed';
  if (reason === reasons.DUPLICATE_IDENTITY) return 'elsewhere';

  return 'lost';
}

/*
 * THE REAL VOICE CONNECTOR — LiveKit, lazily loaded.
 *
 * The client SDK is heavy, so it is imported only when a reader actually joins a room: someone who
 * never opens one never downloads it. It connects with the server-minted token and publishes the
 * microphone only if the token permits it — a listener's token grants no source, so asking would
 * fail (COMMUNICATION.md §5.2).
 *
 * Its behaviour is verified against a running LiveKit, not headlessly; what the tests hold still is
 * the components, through the `VoiceConnector` seam.
 */
/**
 * TURN THE MICROPHONE ON, AND MAKE SURE IT TOOK.
 *
 * `setMicrophoneEnabled` answers with the publication or with `undefined` — the SDK says whether it
 * published — and both places that asked for a microphone were discarding that answer. About one
 * ask in three in the seconds after a permission change resolves without publishing anything.
 *
 * The button was fixed first and the JOIN was not, which is how the live gate caught the other
 * half: a host walked into their own room and arrived muted, because the publish at connect had
 * quietly done nothing. Same defect, same file, two call sites — so it becomes one function.
 *
 * Bounded at three: a publish that has not taken in a second is not going to, and something that
 * retries for ever is worse than something that says so.
 */
const MIC_KEEP_TRYING_FOR = 3000;

/**
 * WAIT FOR THE GRANT TO ARRIVE, then ask — rather than asking repeatedly and hoping.
 *
 * Retrying blindly was the first attempt and it is not enough: the gate still went red about one
 * run in several with a three-second budget of attempts. The SDK will not publish a microphone it
 * believes it has no permission for, and after a promotion this browser learns of the permission by
 * its own path — the roster comes from OUR server and can say "you may speak" before the SFU has
 * told this client anything.
 *
 * So the first thing to wait for is the permission, not the publish. Bounded, because a grant that
 * has not arrived in three seconds is a different failure and the reader is owed a sentence rather
 * than a spinner.
 */
const waitForPermission = async (
  room: import('livekit-client').Room,
  within: number,
): Promise<boolean> => {
  const until = Date.now() + within;
  for (;;) {
    if (room.localParticipant.permissions?.canPublish ?? false) return true;
    if (Date.now() >= until) return false;
    await new Promise(resolve => setTimeout(resolve, 100));
  }
};

const openMicrophone = async (room: import('livekit-client').Room): Promise<boolean> => {
  /*
   * A DEADLINE RATHER THAN A COUNT, and the number is measured rather than chosen.
   *
   * Three tries over half a second was the first attempt and it was not enough: the live gate went
   * green three runs in a row and then red on the fourth, which is exactly what a one-in-three
   * flake looks like when you stop watching too early. Whatever the SFU is doing after a permission
   * change, it can take longer than that.
   *
   * Three seconds is the outer edge of what a reader will wait with a finger still on the button,
   * and it is the whole budget rather than the gap between tries — a count of retries says nothing
   * about how long anybody waited.
   */
  const until = Date.now() + MIC_KEEP_TRYING_FOR;
  /* The grant first: asking before it lands is asking a question whose answer is always no. */
  if (!(await waitForPermission(room, MIC_KEEP_TRYING_FOR))) return false;
  for (let attempt = 0; ; attempt += 1) {
    if (attempt > 0) await new Promise(resolve => setTimeout(resolve, 250));
    /*
     * BOUNDED, because the third thing this call can do is NOTHING AT ALL.
     *
     * It can publish, it can answer `undefined`, and it can never settle — the SDK publishes by
     * bringing up a transport, and a transport that never comes up leaves this awaiting for ever.
     * The press then neither opens a microphone nor produces a sentence, which is the one outcome
     * §0 says a live control must never have. A browser gate printed exactly that three times,
     * and the EMPTY CONSOLE beside it is the evidence: nothing was thrown, so nothing was caught,
     * so nothing was said.
     *
     * The wait is what remains of the budget, with a floor so that a slow grant does not leave
     * the last attempt no time at all. A late publish is not lost: it refreshes the roster when
     * it lands (`LocalTrackPublished`), and the roster is the only thing this product believes
     * about its own microphone — so the button corrects itself rather than lying either way.
     */
    const left = Math.max(until - Date.now(), 250);
    if (await answeredWithin(room.localParticipant.setMicrophoneEnabled(true), left)) return true;
    if (Date.now() >= until) return false;
  }
};

export const livekitConnector: VoiceConnector = {
  /*
   * `WholeSession` AND NOT `VoiceSession` — every member, none optional.
   *
   * The seam's optional members exist for the phone, which has no connector, and for doubles that
   * say only what their case is about. This is the implementation a reader actually runs, so
   * omitting one here is a control that quietly does nothing: the panel guards `if (!session.x)`,
   * every test's fake has `x`, and nothing anywhere goes red. Assignable to `VoiceSession`, so
   * the seam the panels see is unchanged.
   */
  async connect(url: string, token: string): Promise<WholeSession> {
    /*
     * `DisconnectReason` comes from HERE and not from `@livekit/protocol`, though that is where it
     * is declared. A top-level import of the protocol package would be eager, and the whole point
     * of this lazy import is that a reader who never opens a room never downloads the SDK —
     * `rooms.spec.ts` has a browser gate that fails if livekit appears in the first load.
     */
    const { Room, RoomEvent, DisconnectReason } = await import('livekit-client');
    const room = new Room();
    await room.connect(url, token);

    /*
     * Whether we may speak is the TOKEN's answer, not the UI's guess: a listener calling
     * setMicrophoneEnabled would be refused by the SFU, so we simply do not ask.
     */
    const mayPublish = room.localParticipant.permissions?.canPublish ?? false;
    if (mayPublish) {
      /*
       * AND A MICROPHONE WE CANNOT GET IS NOT A REASON TO KEEP SOMEBODY OUT.
       *
       * This was awaited bare, so anything `getUserMedia` refuses — no microphone on the machine, a
       * reader who said no to the prompt, a device another program has hold of — rejected the whole
       * connect. The room never drew and the panel said "That did not work just now": a reader shut
       * out of a conversation they could perfectly well have listened to, told nothing they could
       * act on. Listening is most of what a room is for, and on a shared or borrowed machine
       * declining the microphone is the sensible thing to do.
       *
       * They arrive muted, which the roster reports honestly, and the mic control is theirs to
       * press — which is the moment the browser will ask again.
       */
      /*
       * AND NOT AWAITED, which is the other half and the one that actually shows.
       *
       * The browser asks for the microphone with a dialog, and until the reader answers it this
       * promise does not settle. Awaited, the JOIN waits on it: the room does not draw, the panel
       * says nothing, and the reader is looking at a page that appears to have ignored them while
       * their own browser waits for an answer they have not been given a reason to give. Asked
       * alongside instead — they are in the conversation, and the microphone arrives when it does.
       */
      /* Retried like the button, and for the same reason — a host who arrives muted in their own
         room has no way to know the publish was the thing that failed. */
      /*
       * AND THE ROSTER IS TOLD HERE TOO, which is the case the whole defect started as.
       *
       * The press was fixed first and this was left: the publish AT JOIN is the one §0 describes
       * as *"a host walked into their own room and arrived muted"*, and it refreshes the roster
       * only if `LocalTrackPublished` arrives. `onParticipants` pushes once when the panel
       * subscribes, which covers a publish that finishes first — and this one can take three
       * seconds, so it usually finishes second.
       *
       * Told on BOTH outcomes: a publish that worked and one that never did are each a fact the
       * roster should carry, and "they listen" is a true state rather than an error to hide.
       *
       * `then(a, b)` rather than an awaited call, because this is deliberately not awaited — the
       * browser may be showing a permission dialog, and the room must draw meanwhile.
       */
      void openMicrophone(room).then(
        () => tellTheRoster(),
        () => tellTheRoster(),
      );
    }

    /*
     * WHOSE AUDIO THIS BROWSER ASKED FOR AND DID NOT GET — see `RoomParticipant.unheard`.
     *
     * Kept as a set rather than read off the publications, because "not subscribed" is true of
     * every arrival for the second the offer takes, and a marker that flashed on every join would
     * be noise. A failure is a fact; a pending subscription is not.
     */
    const unheard = new Set<string>();

    /*
     * EVERYBODY WATCHING THE ROSTER, so a change we MADE can be told without waiting to be told.
     *
     * `onParticipants` wires ten SDK events and refreshes on each, which is right for anything
     * that happens to us. It is not enough for what we do ourselves: the roster is the only thing
     * this product believes about its OWN microphone, and if the event for our own publish does
     * not arrive, the button goes on saying "Unmute me" at somebody already audible — with
     * nothing thrown, so nothing said either. §0 calls that the one outcome a live control must
     * never have, and a browser gate has now met it five times.
     *
     * The same shape as `watching` for chat, one screen down.
     */
    const rosterReaders = new Set<(rows: RoomParticipant[]) => void>();

    /*
     * AND THE SAME QUESTION ASKED OF EVERY OTHER THING WE CHANGE OURSELVES — which found this one.
     *
     * `startPlayback` calls `room.startAudio()` and tells nobody. The banner that says **this
     * browser is playing nothing at all** is refreshed by `AudioPlaybackStatusChanged` alone, so
     * if that event does not arrive after a press that worked, the reader presses *play* and the
     * banner stays: a live control that did nothing, with nothing said. The same shape as the
     * microphone one screen up, and worse placed — §0 records this half as **unproven**, because
     * `canPlaybackAudio` needs a live `Room` and headless Chromium never refuses autoplay.
     *
     * Telling them after the call reports the truth either way: if playback is still blocked the
     * banner stays, which is honest, and if it is not the banner goes.
     */
    const playbackReaders = new Set<(blocked: boolean) => void>();

    const roster = (): RoomParticipant[] => {
      const everyone = [room.localParticipant, ...room.remoteParticipants.values()];
      return everyone.map(participant => ({
        identity: participant.identity,
        name: participant.name ?? participant.identity,
        speaking: participant.isSpeaking,
        muted: !participant.isMicrophoneEnabled,
        canSpeak: participant.permissions?.canPublish ?? false,
        unheard: unheard.has(participant.identity),
      }));
    };

    const tellTheRoster = (): void => {
      const rows = roster();
      for (const reader of rosterReaders) reader(rows);
    };

    const tellThePlayback = (): void => {
      const blocked = !room.canPlaybackAudio;
      for (const reader of playbackReaders) reader(blocked);
    };

    /*
     * A COUNTER THIS BROWSER OWNS, and the only thing separating one message from another.
     *
     * It used to move only when WE sent something, so every message received between two of our own
     * sends shared it — and what actually distinguished them was the timestamp in the packet, which
     * the sender chose. Twenty messages claiming one millisecond arrived as twenty identical ids,
     * and a list keying on them reads that as one message changing rather than twenty appearing.
     */
    let delivered = 0;

    /*
     * WHOEVER IS WATCHING THE CONVERSATION — kept here because the reader's OWN messages have to
     * reach them, and the wire will not do it.
     *
     * `RoomEvent.DataReceived` fires for data from other participants and never for your own: the
     * SFU has no reason to send you back what you just sent it. So a reader typed a question into a
     * room of three hundred, the box cleared, and nothing appeared — no message, no error, no way
     * to tell it had gone anywhere. The thing they would do next is type it again.
     *
     * Two things said this was meant to work and neither was the product: `.room-chat-message.mine`
     * has a style of its own in the stylesheet, and the panel's own test emitted a message with the
     * reader's identity through the RECEIVE path — which the SDK never does. The mock made the
     * defect look like the requirement, the same way five panel mocks did with bare Errors.
     */
    const watching = new Set<(message: ChatMessage) => void>();

    return {
      disconnect: () => room.disconnect(),

      /*
       * ASKED AT THE MOMENT IT IS ASKED, and this was a captured value.
       *
       * `mayPublish` is read once, at connect, and it is right there: a listener's token grants no
       * source, so publishing would fail and we simply do not ask. It is wrong HERE, because a
       * grant is not a fact about the connection — it is a fact about right now, and a leader can
       * change it (D22). Somebody who arrived into a silenced room and was later given the floor
       * had the grant, had the roster, had a button offering "Unmute me" — and every press returned
       * early against a value decided before any of it happened. A promoted reader whose microphone
       * could never work again for the life of that connection, with nothing on screen to say so.
       *
       * Found by two browsers in one room (`web/e2e/room-two.spec.ts`), where one of them can be
       * the leader who changes it.
       */
      async setMicrophoneEnabled(enabled: boolean): Promise<void> {
        /* Turning it OFF needs no grant and can publish nothing — a listener asking for silence is
           already silent, and the SDK is happy to be told so. */
        if (!enabled) {
          await room.localParticipant.setMicrophoneEnabled(false);
          /* The same reason as the other branch: a change WE made is told rather than waited
             for. Failing to close leaves a reader audible while believing they are not, which
             `toggleMic` already calls the half worth its own sentence. */
          tellTheRoster();
          return;
        }

        /*
         * NO SILENT RETURN HERE — this line was the last place the press could do nothing at all.
         *
         * It asked `localParticipant.permissions` and gave up quietly when the answer was no. The
         * button above it is enabled from the ROSTER, which comes from our server, and the two
         * learn of a promotion by different paths: for a moment after a leader gives somebody the
         * floor, the roster says "you may speak" and this object still says no. A press in that
         * window returned here — no microphone, no sentence, a live button that did nothing, which
         * is the exact third outcome the browser gate was written to forbid. **The gate caught it.**
         *
         * `openMicrophone` already answers this properly: it waits for the grant to land before
         * asking to publish, and reports when it never does. So the wait replaces the shrug, and a
         * reader who genuinely may not speak — who cannot reach this button, because the roster
         * disables it — would at worst be told after three seconds rather than told nothing.
         */

        /*
         * AND THE SDK ALREADY SAYS WHETHER IT PUBLISHED — `setMicrophoneEnabled` answers with the
         * publication or with `undefined`, and this threw that answer away.
         *
         * That is the whole of the defect §0 has carried as *"the first mic press after being given
         * the floor sometimes does nothing"*: about one press in three in the seconds after a
         * promotion resolves without publishing. The grant is there, the button is live, the SDK
         * says nothing — because it was asked and its answer was discarded by the layer that asked.
         * The browser gate had to press in a loop to get past it, which is the reader's own
         * workaround written into a test.
         *
         * So the press is made to mean what a reader thinks it means: try, and if nothing was
         * published, wait a beat for whatever was mid-flight and try once more. Bounded at three,
         * because a press that has not worked in a second is not going to, and a control that
         * retries for ever is worse than one that says so.
         */
        if (await openMicrophone(room)) {
          /*
           * ASKED AGAIN AT THE MOMENT WE KNOW THE ANSWER CHANGED — which is not the same as
           * waiting for the SDK to mention it.
           *
           * By elimination, this is what the fifth red run said. `told` was false, so nothing
           * threw and `openMicrophone` returned true; the console was EMPTY of every level, so
           * the warning below did not fire and the SDK reported the microphone ENABLED. The
           * panel still drew "Unmute me", and it draws that from the roster — so the roster was
           * the only thing left that could be wrong, and what refreshes it is an event.
           *
           * So the roster is refreshed here rather than hoped for. The ten event subscriptions
           * stay: they are how a change made by SOMEBODY ELSE arrives, and this is only about
           * the one we made ourselves.
           */
          tellTheRoster();
          /*
           * PUBLISHED, AND THE BROWSER STILL SAYS MUTED — the one outcome nothing here can explain,
           * written down rather than guessed at.
           *
           * A browser gate has gone red four times on the same line: *"the press did neither: no
           * microphone and nothing said — aria-pressed=true enabled=true console=[]"*. The empty
           * console is what makes it hard: nothing was thrown, so the press did not fail; and the
           * roster is drawn from `isMicrophoneEnabled`, so it says the microphone is off.
           *
           * The remaining shape is this one: the SDK took the track and the participant still
           * reports it disabled, which is what a MediaStreamTrack that has been muted by the
           * BROWSER looks like — a fake device under load, a real one another program has taken.
           * If that is what is happening, the product is telling the truth and the gate is asking
           * for something this machine cannot give.
           *
           * So: no behaviour changes here, and the next red run carries a cause instead of an
           * empty array. A sentence for the reader belongs after the evidence, not before it —
           * `LocalAudioSilenceDetected` in the SDK inventory is the other half of the same case.
           */
          if (!room.localParticipant.isMicrophoneEnabled) {
            console.warn('[social] the track was published and this browser still reports the microphone muted');
          }
          return;
        }

        /* Three tries and no track: the reader pressed a live button and is still not audible, and
           being told so is the only thing worse than nothing. */
        throw new MicrophoneDidNotOpenError();
      },

      /*
       * A VOLUME KNOB, in this browser and nowhere else. `setVolume(0)` is applied to the local
       * playback of that participant's track — it publishes nothing, tells the SFU nothing, and
       * tells the person nothing. Somebody who is not in the room right now is simply not found,
       * which is the correct no-op: they are already inaudible.
       */
      setParticipantMuted(identity: string, muted: boolean): void {
        room.remoteParticipants.get(identity)?.setVolume(muted ? 0 : 1);
      },

      /**
       * Type into the room, over the data channel the token already grants (D12) — which is why
       * live chat needs no WebSocket gateway, no presence service and no new infrastructure.
       *
       * `reliable` because a dropped question is worse than a late one; chat is not audio.
       */
      async sendMessage(text: string): Promise<void> {
        /* The same rule on the way out as on the way in — one function, so a message this browser
           sends and a message it accepts cannot disagree about what a chat carries. */
        const trimmed = chatTextOf(text);
        if (trimmed === null) return;
        const payload: WirePayload = { kind: 'chat', text: trimmed, at: Date.now() };
        const bytes = new TextEncoder().encode(JSON.stringify(payload));
        await room.localParticipant.publishData(bytes, { reliable: true });

        /*
         * AFTER the publish, never before: a message shown and not sent is the worse lie of the
         * two, because the reader stops waiting for an answer that nobody was ever asked for. If
         * the publish rejects, this line is not reached and the panel gives the words back.
         */
        const me = room.localParticipant;
        delivered += 1;
        for (const listener of watching) {
          listener({
            id: chatIdOf(me.identity, delivered),
            identity: me.identity,
            name: me.name ?? me.identity,
            text: payload.text,
            at: payload.at,
          });
        }
      },

      onMessage(listener): () => void {
        const handle = (bytes: Uint8Array, from?: { identity: string; name?: string }): void => {
          const payload = readPayload(bytes);
          if (!payload || payload.kind !== 'chat') return;
          const identity = from?.identity ?? 'unknown';
          delivered += 1;
          listener({
            id: chatIdOf(identity, delivered),
            identity,
            name: from?.name ?? identity,
            text: payload.text,
            /*
             * WHEN IT ARRIVED, not when the sender says it was written. We cannot check
             * somebody else's clock, and the only thing trusting it buys is a lie a reader
             * might one day be shown, since a message carries a time and a surface may render
             * it. Our own message above keeps its own stamp, which is genuinely ours.
             */
            at: Date.now(),
          });
        };
        room.on(RoomEvent.DataReceived, handle);
        /* And the same listener hears what THIS reader says — see `watching` above. */
        watching.add(listener);
        return () => {
          room.off(RoomEvent.DataReceived, handle);
          watching.delete(listener);
        };
      },

      /*
       * ONLY THE LISTENING HALF. This browser used to SEND the nudge too — whoever pressed the
       * button announced their own change to the rest of the room — and that worked exactly as long
       * as the person who changed the room was in it, which is a fact about the actor rather than
       * about the change. A second Hall's leadership accepting a co-host invitation, and a site
       * moderator acting from the moderation screen (D18), are both changes made by somebody who is
       * not here, and neither reached anybody.
       *
       * So the server announces now, over the same channel and with the same packet
       * (`core/room-wire.ts`), because it is the side that knows a room changed: it just wrote it.
       * The nudge arrives from no participant at all, which this handler already ignored.
       */
      onChangeHint(listener): () => void {
        const handle = (bytes: Uint8Array): void => {
          const payload = readPayload(bytes);
          if (payload?.kind === ROOM_CHANGED) listener();
        };
        room.on(RoomEvent.DataReceived, handle);
        return () => { room.off(RoomEvent.DataReceived, handle); };
      },

      /*
       * OUR OWN CONNECTION, which is the one the roster below cannot report on.
       *
       * `Reconnecting` and `Reconnected` are the SDK doing its job — a brief gap on a train, and
       * the reader wants to know why nobody is talking rather than to be thrown out. `Disconnected`
       * is it having finished trying, and is terminal: at that point the SFU has also told our
       * server the participant left, so continuing to draw a room the reader is not in is a lie
       * about a live microphone.
       */
      onConnection(listener): () => void {
        type DisconnectCode = typeof DisconnectReason[keyof typeof DisconnectReason];
        const live = (): void => listener('live');
        const going = (): void => listener('reconnecting');
        /*
         * WHY, not just that. LiveKit hands the reason to `Disconnected` and three of the four are
         * not network trouble at all — a host removing somebody, a host closing the room, and the
         * same account arriving on a second device. Reporting "connection lost" for any of those is
         * false, and for the first it hides a moderation decision from the only person it is about.
         *
         * `DisconnectReason` is imported for its VALUES, so it cannot be a type-only import — the
         * numbers are the wire format and writing 4 and 5 here would be the enum copied by hand.
         */
        const gone = (reason?: DisconnectCode): void => {
          listener('dropped', departureFrom(reason, DisconnectReason));
        };
        room.on(RoomEvent.Reconnecting, going);
        room.on(RoomEvent.Reconnected, live);
        room.on(RoomEvent.Disconnected, gone);
        listener('live');
        return () => {
          room.off(RoomEvent.Reconnecting, going);
          room.off(RoomEvent.Reconnected, live);
          room.off(RoomEvent.Disconnected, gone);
        };
      },

      /*
       * WHETHER THIS BROWSER IS PLAYING ANYTHING AT ALL — the one silence this layer could not
       * explain, and the one that looks most like the product working.
       *
       * `AudioPlaybackStatusChanged` is one of fifty events the SDK emits and one of the few that
       * matters here: browsers refuse to play audio no gesture asked for, and when they refuse,
       * everything else looks right. The connection is live, the roster fills, the speaker meters
       * move — and the reader hears nothing. Safari suspends playback on a backgrounded tab, iOS
       * more readily still, and a reconnect can land in the same state.
       *
       * The current answer goes out on subscribe as well, because a reader who arrives already
       * blocked never sees it CHANGE — the same reason `onConnection` says `live` immediately and
       * `onParticipants` pushes before any event.
       */
      /*
       * WHERE THE ROOM IS LOOKING — §11, over the channel the chat and the nudge already use.
       *
       * The sender comes from the SDK rather than from the packet, which is the point: a packet
       * says what it likes and `from` is what the SFU knows. A move with no participant behind it
       * — the server's own nudge rides this channel — is not attributable and is dropped here, so
       * the panel above never has to wonder whether nobody may drive.
       */
      onFollow(listener): () => void {
        const handle = (bytes: Uint8Array, from?: { identity: string }): void => {
          const view = readFollow(bytes);
          if (view === null || !from?.identity) return;
          listener(from.identity, view);
        };
        room.on(RoomEvent.DataReceived, handle);
        return () => { room.off(RoomEvent.DataReceived, handle); };
      },

      async sendFollow(view: FollowView): Promise<void> {
        await room.localParticipant.publishData(followPacket(view, Date.now()), { reliable: true });
      },

      /*
       * THE SDK CHECKS FOR SILENCE WHEN A MICROPHONE IS PUBLISHED, and says so once. It is not a
       * meter: it is a single look at the track the moment it goes up, which is why this carries
       * no value and the panel keeps the answer until the reader presses again.
       */
      onSilentMicrophone(listener): () => void {
        const heard = (): void => listener();
        room.on(RoomEvent.LocalAudioSilenceDetected, heard);
        return () => { room.off(RoomEvent.LocalAudioSilenceDetected, heard); };
      },

      onPlaybackBlocked(listener): () => void {
        const push = (): void => listener(!room.canPlaybackAudio);
        /* Also reachable without an event — see `tellThePlayback`. */
        playbackReaders.add(listener);
        room.on(RoomEvent.AudioPlaybackStatusChanged, push);
        push();
        return () => {
          playbackReaders.delete(listener);
          room.off(RoomEvent.AudioPlaybackStatusChanged, push);
        };
      },

      /* Only a gesture may lift it, so this is called from a press and never on a timer. */
      async startPlayback(): Promise<void> {
        await room.startAudio();
        /* Told rather than waited for — see `playbackReaders`. */
        tellThePlayback();
      },

      onParticipants(listener): () => void {
        const push = (): void => listener(roster());
        /* Also reachable without an event — see `tellTheRoster`. */
        rosterReaders.add(listener);
        /* Every event that can change who is present, who is talking, or who is muted — including
           a host's force-mute, which arrives as a track-mute on someone else. */
        const events = [
          RoomEvent.ParticipantConnected,
          RoomEvent.ParticipantDisconnected,
          RoomEvent.ActiveSpeakersChanged,
          RoomEvent.TrackMuted,
          RoomEvent.TrackUnmuted,
          RoomEvent.TrackPublished,
          RoomEvent.TrackUnpublished,
          RoomEvent.ParticipantPermissionsChanged,
          /*
           * AND OUR OWN TRACK, which is a DIFFERENT event and was missing — the cause of a flake
           * that cost three red runs across two gates.
           *
           * `TrackPublished` is emitted for REMOTE participants. When this reader's own microphone
           * is published for the first time the SDK emits `LocalTrackPublished`, and nothing here
           * was listening. So the roster — which is the single source of truth for whether our own
           * microphone is live — did not refresh: the button went on saying "Unmute me" at somebody
           * who was already audible, and because the press had SUCCEEDED nothing was said either.
           * That is the exact pair the browser gate forbids: no microphone visible and no sentence.
           *
           * It was intermittent because almost anything else refreshes the roster a moment later —
           * somebody speaking, somebody joining, a later mute. A quiet two-browser room is where
           * nothing else happens, which is why the gate saw it and a reader in a busy room would
           * not have.
           *
           * The mirror of this is already in §0: *"a fake whose setMicrophoneEnabled changed nothing
           * let the component keep its own flag and pass, which is how the button came to say Mute
           * at a reader nobody could hear."* Same defect, other direction, same cause — the state
           * shown was not the state that changed.
           */
          RoomEvent.LocalTrackPublished,
          RoomEvent.LocalTrackUnpublished,
        ] as const;
        for (const event of events) room.on(event, push);

        /*
         * AND WHOSE AUDIO NEVER ARRIVED. These two are not about who is in the room — they are
         * about whether THIS browser got what it asked for, which nothing else here can see: the
         * SFU goes on reporting a failed-to-subscribe participant as speaking, because from the
         * server's side they are.
         */
        const failed = (_sid: string, participant: { identity: string }): void => {
          unheard.add(participant.identity);
          push();
        };
        const arrived = (_track: unknown, _pub: unknown, participant: { identity: string }): void => {
          if (!unheard.delete(participant.identity)) return;
          push();
        };
        const left = (participant: { identity: string }): void => { unheard.delete(participant.identity); };
        room.on(RoomEvent.TrackSubscriptionFailed, failed);
        room.on(RoomEvent.TrackSubscribed, arrived);
        room.on(RoomEvent.ParticipantDisconnected, left);

        push();
        return () => {
          rosterReaders.delete(listener);
          for (const event of events) room.off(event, push);
          room.off(RoomEvent.TrackSubscriptionFailed, failed);
          room.off(RoomEvent.TrackSubscribed, arrived);
          room.off(RoomEvent.ParticipantDisconnected, left);
        };
      },
    };
  },
};
